import type { AxiosInstance } from 'axios';
import { apiClient } from '../client/client';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { PaginatedList } from '../types/common/PaginatedList';
import type {
  AdminActionLogListItemViewModel,
  AdminActionLogQueryFilter,
} from '../types/adminActionLog';

/**
 * Read-side API client for the Super-Admin-only admin audit log
 * (`GET /api/admin/action-log` — 011, US3). The backend role gate is
 * `[Authorize(Roles = "Super Admin")]`; 403 is handled here by surfacing
 * the standard `ServiceResultT` unsuccessful envelope rather than throwing.
 *
 * See `specs/011-dark-mode-admin-audit/contracts/admin-action-log.http.md`.
 */
export class AdminActionLogService {
  private static readonly BASE = '/api/admin/action-log';
  private static readonly MIN_PAGE_SIZE = 1;
  private static readonly MAX_PAGE_SIZE = 200;

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async list(
    filter: AdminActionLogQueryFilter,
  ): Promise<ServiceResultT<PaginatedList<AdminActionLogListItemViewModel>>> {
    const params = AdminActionLogService.buildParams(filter);

    try {
      const { data } = await this.client.get<
        ServiceResultT<PaginatedList<AdminActionLogListItemViewModel>>
      >(AdminActionLogService.BASE, { params });
      return data;
    } catch (err: unknown) {
      const res = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number; data?: unknown } }).response
        : undefined;

      if (res?.status === 403) {
        return {
          isSuccess: false,
          statusCode: 403,
          message: 'Bu sayfayı görüntüleme yetkiniz yok.',
          data: null,
        };
      }

      throw err;
    }
  }

  private static buildParams(filter: AdminActionLogQueryFilter): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    if (filter.actionType) out.actionType = filter.actionType;
    if (filter.actorUserId) out.actorUserId = filter.actorUserId;
    if (filter.targetType) out.targetType = filter.targetType;
    if (filter.targetId) out.targetId = filter.targetId;
    if (filter.from) out.from = filter.from;
    if (filter.to) out.to = filter.to;
    if (typeof filter.pageNumber === 'number' && filter.pageNumber > 0) {
      out.pageNumber = filter.pageNumber;
    }
    if (typeof filter.pageSize === 'number' && filter.pageSize > 0) {
      out.pageSize = Math.max(
        AdminActionLogService.MIN_PAGE_SIZE,
        Math.min(AdminActionLogService.MAX_PAGE_SIZE, filter.pageSize),
      );
    }
    return out;
  }
}

export const adminActionLogService = new AdminActionLogService();

/** Convenience wrapper matching the task signature from 011-tasks T034. */
export async function listAdminActionLogs(
  filter: AdminActionLogQueryFilter,
): Promise<ServiceResultT<PaginatedList<AdminActionLogListItemViewModel>>> {
  return adminActionLogService.list(filter);
}
